import React, { useMemo, useState } from "react";
import { Box, Typography, Tooltip as MuiTooltip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { summaryCardTheme as t } from "./styles";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";

function SummaryFrameworkComplianceChart({ overall, framework, onFrameworkChange }) {
  const [expandedFramework, setExpandedFramework] = useState(null);
  const data = useMemo(() => {
    const list = Array.isArray(overall?.compliance_by_framework)
      ? overall.compliance_by_framework
      : [];

    return list
      .filter((fw) => (fw.controls_failed_total ?? 0) > 0)
      .map((fw) => ({
        name: fw.framework || "—",
        arns: fw.arns_failed_total ?? 0,
        controls: fw.controls_failed_total ?? 0,
        failedControlIds: Array.isArray(fw.failed_control_ids)
          ? fw.failed_control_ids
          : [],
      }))
      .sort((a, b) => b.controls - a.controls);
  }, [overall]);

  const maxLegendItems = 6;

  const sliceColors = useMemo(() => {
    const base = t.colors.chart || [];
    const fallback = ["#6366F1", "#F97316", "#22C55E", "#EC4899", "#06B6D4", "#FACC15"];
    return data.map((_, idx) => base[idx % base.length] || fallback[idx % fallback.length]);
  }, [data]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;

    const iconSrc = getComplianceIcon(d.name);

    // Hover-only: show brief metrics so tooltip can disappear without affecting interaction.
    return (
      <Box
        sx={{
          ...t.chartTooltip,
          fontFamily: t.fontFamily,
          maxWidth: 260,
          p: 1.25,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.25,
          }}
        >
          {iconSrc && (
            <Box
              component="img"
              src={iconSrc}
              alt={d.name}
              sx={{
                width: 18,
                height: 18,
                borderRadius: 1,
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            component="span"
            sx={{
              fontWeight: 600,
              color: t.colors.text,
              fontSize: "0.78rem",
            }}
          >
            {d.name}
          </Typography>
        </Box>
        <Typography
          component="div"
          sx={{ color: t.colors.textMuted, fontSize: "0.73rem" }}
        >
          {d.arns} ARNs with findings · {d.controls} failed controls
        </Typography>
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
          fontFamily: t.fontFamily,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No compliance framework findings
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        fontFamily: t.fontFamily,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: t.colors.text,
          fontSize: "0.875rem",
          mb: 1,
          fontFamily: t.fontFamily,
        }}
      >
        Compliance frameworks
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={data}
            dataKey="controls"
            nameKey="name"
            innerRadius={55}
            outerRadius={80}
            paddingAngle={3}
            cursor={onFrameworkChange ? "pointer" : "default"}
             onClick={(slice) => {
               const clickedName = slice?.name;
               const payload = slice?.payload;
               if (!clickedName || !payload) return;

               // Toggle external framework filter if provided.
               if (onFrameworkChange) {
                 onFrameworkChange(
                   framework && framework === clickedName ? "" : clickedName
                 );
               }

               // Also toggle expanded details panel for this framework.
               setExpandedFramework((prev) =>
                 prev && prev.name === clickedName ? null : payload
               );
             }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${entry.name}-${index}`}
                fill={sliceColors[index]}
                stroke={
                  framework && framework === entry.name
                    ? t.colors.accent
                    : t.colors.card
                }
                strokeWidth={framework && framework === entry.name ? 2 : 1}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>

      {expandedFramework && (
        <Box
          sx={{
            mt: 1.5,
            borderRadius: t.radius.lg,
            border: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
            boxShadow: t.shadow,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${t.colors.border}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {getComplianceIcon(expandedFramework.name) && (
                <Box
                  component="img"
                  src={getComplianceIcon(expandedFramework.name)}
                  alt={expandedFramework.name}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 1,
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
              )}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: t.colors.text,
                  }}
                >
                  {expandedFramework.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: t.colors.textMuted,
                  }}
                >
                  {expandedFramework.arns} ARNs with findings ·{" "}
                  {expandedFramework.controls} failed controls
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setExpandedFramework(null)}
              sx={{
                color: t.colors.textMuted,
                "&:hover": {
                  color: t.colors.text,
                  backgroundColor: t.colors.borderLight,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {Array.isArray(expandedFramework.failedControlIds) &&
            expandedFramework.failedControlIds.length > 0 && (
              <Box
                sx={{
                  maxHeight: 220,
                  overflowY: "auto",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: t.colors.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.5,
                  }}
                >
                  Control IDs
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: t.colors.text,
                    wordBreak: "break-word",
                  }}
                >
                  {expandedFramework.failedControlIds.join(", ")}
                </Typography>
              </Box>
            )}
        </Box>
      )}
      {/* Legend */}
      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {data.slice(0, maxLegendItems).map((item, idx) => {
          const isActive = framework && framework === item.name;
          const clickable = typeof onFrameworkChange === "function";
          return (
            <Box
              key={item.name}
              onClick={() => {
                if (clickable) {
                  onFrameworkChange(isActive ? "" : item.name);
                }
                setExpandedFramework((prev) =>
                  prev && prev.name === item.name ? null : item
                );
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                mr: 1,
                cursor: "pointer",
                opacity: framework && !isActive ? 0.6 : 1,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: sliceColors[idx],
                  mr: 0.75,
                  boxShadow: isActive
                    ? `0 0 0 2px ${t.colors.border}`
                    : "none",
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: t.colors.textMuted,
                  fontFamily: t.fontFamily,
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {item.name}
              </Typography>
            </Box>
          );
        })}
        {data.length > maxLegendItems && (
          <MuiTooltip
            title={
              <Box
                sx={{
                  p: 1.5,
                  maxHeight: 240,
                  maxWidth: 280,
                  overflowY: "auto",
                  fontFamily: t.fontFamily,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: t.colors.text,
                    mb: 0.5,
                  }}
                >
                  All compliance frameworks
                </Typography>
                {data.map((fw, idx) => {
                  const iconSrc = getComplianceIcon(fw.name);
                  return (
                    <Box
                      key={`${fw.name}-${idx}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      {iconSrc && (
                        <Box
                          component="img"
                          src={iconSrc}
                          alt={fw.name}
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: 1,
                            objectFit: "contain",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: t.colors.text,
                          fontFamily: t.fontFamily,
                        }}
                      >
                        {fw.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            }
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 3,
                  pointerEvents: "auto",
                },
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: t.colors.textMuted,
                fontFamily: t.fontFamily,
                cursor: "default",
              }}
            >
              +{data.length - maxLegendItems} more
            </Typography>
          </MuiTooltip>
        )}
      </Box>
    </Box>
  );
}

export default SummaryFrameworkComplianceChart;

